import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {NavBar, Popup} from 'antd-mobile';
import {LeftOutline} from 'antd-mobile-icons';
import {getNavBarContainerStyle} from "../../../utils/deviceUtils";
import { useTranslation } from 'react-i18next';
import {closeNavigationModals as closeNavigationModalsUtil, forceCloseNavigationModal, emitCloseNavigationEvent} from '../../../utils/navigationUtils';
import {useDeliveryTaskOrderList, useRouteColorMap} from '../store/deliveryStore';
import MapboxMap from '../../../components/Map/MapboxMapView';
import EmptyState from '../../../components/EmptyState';
import OrderDetailCard, { OrderDetailCardRef } from './components/OrderDetailCard';
import AddressList from './components/AddressList';
import AccessInfoDetailView from './components/AccessInfoDetailView';
import AccessCodeFormView from './components/AccessCodeFormView';
import {sortClusterOrders} from "../../../utils/stopNumberUtils";
import PODAbnormalAlert from '../../../components/PODAbnormalAlert';
import { DeliveringPath, DeliveryDetailPath } from '../../../enums/DeliveryNavigatePathEnum';
import { setShouldCheckPendingCodes } from './utils/accessCodeValidationState';
import {useLazyQuery} from '@apollo/client';
import {ANALYZE_ADDRESS_SIMPLE, AnalyzeAddressSimpleResponse} from '../../../graphql/queries/addressQueries';
import {useConfigEnable} from '../../../graphql/queries/commonConfigQueries';
import {GRAY_CONFIG_NAVIGATION_ADDRESS_ANALYZE} from '../../../constants/grayConfigKeys';
import { MapBoxPageMarker } from '../../../constants/mapBoxTrackLog';

// 10种不同deliveryTaskId颜色
const MARKER_COLORS = [
    '#000000', '#668B8B', '#2F4F4F', '#483D8B', '#BEBEBE',
    '#548B54', '#8B864E', '#CD9B1D', '#A2B5CD', '#8B8B7A',
];


/**
 * ⚠️ TODO() 派送端 “地图模式” web版只维护到7.1.0，已迁移原生（这个页面不要再更新了）
 */


// 派送失败的颜色
const FAILED_DELIVERY_COLOR = '#EF4444';

// 默认颜色
const DEFAULT_MARKER_COLOR = '#f5f5f5';

const MapModePage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    // 获取当前司机派送的订单列表
    const { deliveryTaskOrderList } = useDeliveryTaskOrderList();
    const { getOrAssignRouteColor } = useRouteColorMap();

    // 在组件挂载时检查进入来源
    const [isFromDeliveringList] = useState(() => {
        const entrySource = sessionStorage.getItem('mapModeEntrySource');
        if (entrySource === 'delivering-list') {
            sessionStorage.removeItem('mapModeEntrySource');
            return true;
        }
        return false;
    });
    // Popup 控制状态
    const [popupVisible, setPopupVisible] = useState(false);
    const [selectedMarkerData, setSelectedMarkerData] = useState<any>(null);
    const [hasRestoredPopup, setHasRestoredPopup] = useState(false);
    // 视图模式：'address' 显示地址列表，'card' 显示卡片列表
    const [viewMode, setViewMode] = useState<'address' | 'card'>('card');
    // Popup 内容视图：'order' 显示订单详情，'accessCode' 显示门禁详情，'addForm' 显示新增表单，'updateForm' 显示更新表单
    const [popupContentView, setPopupContentView] = useState<'order' | 'accessCode' | 'addForm' | 'updateForm'>('order');
    // 门禁详情页需要的参数
    const [accessCodeViewParams, setAccessCodeViewParams] = useState<{
        orderId: number;
        addressId?: number;
        trackingNumber?: string;
        address?: string;
        addressLat?: number;
        addressLon?: number;
    } | null>(null);
    // 表单页需要的参数
    const [formViewParams, setFormViewParams] = useState<{
        mode: 'add' | 'update';
        code?: any; // AccessCode
        orderId: number;
        addressId?: number;
        trackingNumber?: string;
        address?: string;
        addressLat?: number;
        addressLon?: number;
    } | null>(null);
    // 刷新触发器，用于触发门禁详情页刷新
    const [accessCodeRefreshTrigger, setAccessCodeRefreshTrigger] = useState(0);
    // 地址分析相关状态
    const [analyzedAddressLine1, setAnalyzedAddressLine1] = useState<string | undefined>(undefined);
    const [analyzeAddress, { loading: analyzingAddress }] = useLazyQuery<AnalyzeAddressSimpleResponse>(ANALYZE_ADDRESS_SIMPLE);
    // 导航地址标准化功能灰度配置
    const { isEnabled: isNavigationAddressAnalyzeEnabled, loading: loadingGrayConfig } = useConfigEnable(GRAY_CONFIG_NAVIGATION_ADDRESS_ANALYZE);

    // 保存选中的标记数据到 sessionStorage，以便返回时恢复
    const saveSelectedMarkerToStorage = useCallback((data: any) => {
        sessionStorage.setItem('selectedMarkerData', JSON.stringify(data));
    }, []);

    const getSelectedMarkerFromStorage = useCallback(() => {
        const saved = sessionStorage.getItem('selectedMarkerData');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (error) {
                console.warn('Failed to parse saved marker data:', error);
            }
        }
        return null;
    }, []);

    const clearSelectedMarkerFromStorage = useCallback(() => {
        sessionStorage.removeItem('selectedMarkerData');
        sessionStorage.removeItem('selectAddressSession');
        sessionStorage.removeItem('originalClusterAddresses');
    }, []);

    // 过滤出有效的订单（派送中或派送失败）
    const filterValidClusterAddresses = (clusterAddresses: any[]): any[] => {
        return clusterAddresses.filter((order: any) => {
            const currentOrder = deliveryTaskOrderList.find(item => item.id === order.deliveryTaskOrderId && !item.isTransferOut);
            if (currentOrder) {
                const isDeliveryFailed = currentOrder.isAbnormal && !currentOrder.orderWithAddress?.order?.isReturnToWhsScanned && currentOrder.isDelivery && !currentOrder.orderWithAddress?.order?.isDelivered && !currentOrder.orderWithAddress?.order?.isFinished;
                const isDelivering = currentOrder.isPickedUp && !currentOrder.isAbnormal && currentOrder.isDelivery && !currentOrder.orderWithAddress?.order?.isDelivered && !currentOrder.orderWithAddress?.order?.isFinished;
                return isDeliveryFailed || isDelivering;
            }
            return false;
        });
    };

    // 处理配送地址数据
    const deliveryAddresses = useMemo(() => {
        // 首先进行与列表页面相同的初始过滤
        const deliveringData = deliveryTaskOrderList.filter(item => {
            return (item.isPickedUp || item.isAbnormal || item.isDelivery) && !item.isTransferOut;
        });

        // 然后进行与列表页面相同的二次过滤
        const filteredOrders = deliveringData.filter(order => {
            // 派送中：已取件且非异常且未派送成功
            const isDelivering = order.isPickedUp && !order.isAbnormal && order.isDelivery && !order.orderWithAddress?.order?.isDelivered && !order.orderWithAddress?.order?.isFinished;
            // 派送失败：异常且未派送成功且未扫描退回仓库
            const isDeliveryFailed = order.isAbnormal && !order.orderWithAddress?.order?.isReturnToWhsScanned && order.isDelivery && !order.orderWithAddress?.order?.isDelivered && !order.orderWithAddress?.order?.isFinished;
            
            return isDelivering || isDeliveryFailed;
        });

        // 使用全局 store 中的 routeColorMap，首次进入按 palette 分配，后续返回复用
        const addresses = filteredOrders
            .filter(order => order.orderWithAddress?.deliveryAddress)
            .map(order => {
                // 获取deliveryTaskId
                const deliveryTaskId = order.deliveryTaskId ? order.deliveryTaskId.toString() : 'default';
                
                // 为所有订单分配颜色
                let localMarkerColor = undefined;
                
                if (order.isAbnormal) {
                    // 派送失败的订单使用固定颜色
                    localMarkerColor = FAILED_DELIVERY_COLOR;
                } else {
                    // 非异常的订单根据deliveryTaskId分配颜色（相同deliveryTaskId使用相同颜色）
                    localMarkerColor = getOrAssignRouteColor(deliveryTaskId, MARKER_COLORS);
                }

                // 使用与HereMapView相同的地址格式，包含完整地址信息
                const address = [
                    order.orderWithAddress.deliveryAddress.addressLine1 || '',
                    order.orderWithAddress.deliveryAddress.addressLine2 || '',
                    order.orderWithAddress.deliveryAddress.city || '',
                    order.orderWithAddress.deliveryAddress.state || '',
                    order.orderWithAddress.deliveryAddress.zipcode || '',
                    'US'
                ].filter(Boolean).join(' ');

                return {
                    id: order.id.toString(),
                    address: address,
                    trackingNumber: order.trackingNumber,
                    deliveryTaskOrderId: order.id,
                    stopNumber: order.stopNumber,
                    subStopNumber: order.subStopNumber,
                    isAbnormal: order.isAbnormal, // 异常状态字段
                    localMarkerColor, // 新增颜色字段
                    latitude: order.orderWithAddress?.deliveryAddress?.latitude,
                    longitude: order.orderWithAddress?.deliveryAddress?.longitude
                };
            })
            .sort((a, b) => {
                // 首先按照routeNumber排序（如果有的话）
                // 然后按照stopNumber排序，确保与列表中的顺序一致
                return a.stopNumber - b.stopNumber;
            });

        return addresses;
    }, [deliveryTaskOrderList]);

    // 地图引用，用于直接操作地图
    const mapRef = useRef<any>(null);
    const orderDetailCardRef = useRef<OrderDetailCardRef>(null);
    
    // 保存地图状态到 sessionStorage
    const saveMapState = useCallback(() => {
        if (mapRef.current) {
            const state = {
                center: [mapRef.current.getCenter().lng, mapRef.current.getCenter().lat],
                zoom: mapRef.current.getZoom(),
                timestamp: Date.now()
            };
            sessionStorage.setItem('mapModePageState', JSON.stringify(state));
            console.log('Saved map state:', state);
        }
    }, []);

    // 获取保存的地图状态
    const getSavedMapState = () => {
        if (isFromDeliveringList) {
            return null;
        }
        const savedState = sessionStorage.getItem('mapModePageState');
        if (savedState) {
            try {
                const state = JSON.parse(savedState);
                return state;
            } catch (error) {
                console.warn('Failed to parse saved map state:', error);
            }
        }
        return null;
    };

    // 处理标记点击事件 - 显示弹窗
    const handleMarkerClick = (markerData: any) => {
        // 当切换stop时，重置popup内容视图为订单详情页
        // 这样可以避免显示上一个stop的code详情页
        setPopupContentView('order');
        setAccessCodeViewParams(null);
        setFormViewParams(null);
        
        // 关闭长按导航弹窗
        handleCloseNavigationModals();
        
        if (markerData.isCluster) {
            const addresses = markerData.clusterAddresses;
            // 检查是否有多个不同的地址
            const uniqueAddresses = new Set(addresses.map((addr: any) => addr.address));
            const hasMultipleAddresses = uniqueAddresses.size > 1;
            
            const freshMarkerData = {
                ...markerData,
                clusterAddresses: addresses,
                clusterCount: addresses.length,
                isCluster: true,
                isShowNavigationBar: false
            };
            setSelectedMarkerData(freshMarkerData);
            setViewMode(hasMultipleAddresses ? 'address' : 'card');
            setPopupVisible(true);
            saveSelectedMarkerToStorage(freshMarkerData);
        } else {
            const singleMarkerData = {
                ...markerData,
                isCluster: false
            };
            setSelectedMarkerData(singleMarkerData);
            setViewMode('card');
            setPopupVisible(true);
            saveSelectedMarkerToStorage(singleMarkerData);
        }
        // 新增：强制滚动到顶部
        setTimeout(() => {
            // 方法1：查找 Popup 内的滚动容器
            const popupBody = document.querySelector('.adm-popup-body');
            if (popupBody) {
                popupBody.scrollTop = 0;
            }

            // 方法2：查找内部的滚动容器
            const scrollContainer = document.querySelector('.max-h-\\[70vh\\]');
            if (scrollContainer) {
                scrollContainer.scrollTop = 0;
            }

            // 方法3：直接查找所有可滚动元素
            const scrollableElements = document.querySelectorAll('*');
            scrollableElements.forEach(element => {
                if (element.scrollHeight > element.clientHeight) {
                    element.scrollTop = 0;
                }
            });
        }, 0);
    };

    // 处理地址列表项点击 - 显示该地址对应的所有订单（集群场景）
    const handleAddressClick = (address: any) => {
        if (selectedMarkerData?.clusterAddresses) {
            // 先对 clusterAddresses 进行校验，过滤出有效的订单
            const validClusterAddresses = filterValidClusterAddresses(selectedMarkerData.clusterAddresses);
            
            // 从校验后的地址列表中筛选出当前地址的订单
            const ordersForThisAddress = validClusterAddresses.filter(
                (addr: any) => addr.address === address.address
            );
            
            if (ordersForThisAddress.length > 0) {
                const updatedMarkerData = {
                    ...selectedMarkerData,
                    clusterAddresses: ordersForThisAddress,
                    clusterCount: ordersForThisAddress.length,
                    isCluster: true,
                    isShowNavigationBar: true
                };
                setSelectedMarkerData(updatedMarkerData);
                sessionStorage.setItem("selectAddressSession", address.address);
                // 保存校验后的原始地址列表数据，用于返回
                sessionStorage.setItem("originalClusterAddresses", JSON.stringify(validClusterAddresses));
                setViewMode('card');
            }
        }
    };

    // 处理从卡片返回到地址列表
    const handleBackToAddressList = () => {
        const originalAddresses = sessionStorage.getItem("originalClusterAddresses");
        if (originalAddresses && selectedMarkerData) {
            try {
                const originalClusterAddresses = JSON.parse(originalAddresses);
                // 使用最新的 deliveryTaskOrderList 进行校验，过滤出有效的订单
                const validClusterAddresses = filterValidClusterAddresses(originalClusterAddresses);
                
                const updatedMarkerData = {
                    ...selectedMarkerData,
                    clusterAddresses: validClusterAddresses,
                    clusterCount: validClusterAddresses.length,
                    isCluster: true,
                };
                setSelectedMarkerData(updatedMarkerData);
                setViewMode('address');
                sessionStorage.removeItem("originalClusterAddresses");
            } catch (error) {
                console.warn('Failed to parse original cluster addresses:', error);
            }
        }
    };

    // 处理地图点击事件 - 关闭弹窗和长按导航弹窗
    const handleMapClick = () => {
        handleClosePopup();
    };

    // 统一关闭导航弹窗逻辑（iOS 18 增强修复）
    const handleCloseNavigationModals = useCallback(() => {
        // 方式1：通过全局事件重置所有卡片组件的状态（解决集群模式问题）
        emitCloseNavigationEvent();
        // 方式2：单个订单模式：通过 ref 关闭
        orderDetailCardRef.current?.closeLongPressModal();
        // 方式3：集群模式：直接调用工具函数关闭所有导航弹窗（确保集群模式下也能关闭，iOS 18 增强）
        closeNavigationModalsUtil();
        
        // iOS 18 需要更强制的方式关闭弹窗，使用 requestAnimationFrame 确保状态更新后再执行 DOM 操作
        requestAnimationFrame(() => {
            forceCloseNavigationModal(
                t('pages.delivery.orderDetail.selectMapForThisTimeTitle') || undefined,
                t('pages.delivery.orderDetail.mapCancel') || undefined
            );
        });
    }, [t]);

    // 统一关闭弹窗逻辑（包含 iOS 18 增强修复）
    const handleClosePopup = useCallback(() => {
        // 精确关闭导航弹窗（同步执行，确保导航弹窗状态被正确清除）
        handleCloseNavigationModals();

        setPopupVisible(false);
        setSelectedMarkerData(null);
        setViewMode('card');
        setPopupContentView('order'); // Reset to order view on close
        setAccessCodeViewParams(null); // Clear access code params
        setFormViewParams(null); // Clear form params
        clearSelectedMarkerFromStorage();
        setHasRestoredPopup(false);
        sessionStorage.removeItem('originalClusterAddresses');
    }, [handleCloseNavigationModals, clearSelectedMarkerFromStorage]);

    // 按钮点击前的回调 - 保存地图状态
    const handleBeforeNavigate = useCallback(() => {
        console.log('按钮点击，即将跳转，保存当前地图状态');
        saveMapState();
    }, [saveMapState]);

    // 处理返回导航逻辑
    const handleBackNavigation = useCallback(() => {
        // 检查是否从DriverViolationReviewPage返回
        const fromDriverViolationReview = location.state?.from === 'DriverViolationReviewPage';

        if (fromDriverViolationReview) {
            // 从DriverViolationReviewPage返回，直接跳转到delivering页面
            navigate(DeliveringPath.DELIVERY_DELIVERING);
        } else {
            // 默认使用浏览器历史记录返回
            navigate(-1);
        }
    }, [navigate, location.state]);

    // 处理导航到门禁详情（地图模式）
    const handleNavigateToAccessCodeDetail = useCallback((params: {
        orderId: number;
        addressId?: number;
        trackingNumber?: string;
        address?: string;
        addressLat?: number;
        addressLon?: number;
    }) => {
        console.log('切换到门禁详情视图', params);
        setAccessCodeViewParams(params);
        setPopupContentView('accessCode');
    }, []);

    // 处理导航到新增表单（地图模式）
    const handleNavigateToAddForm = useCallback((params: {
        orderId: number;
        addressId?: number;
        trackingNumber?: string;
        address?: string;
        addressLat?: number;
        addressLon?: number;
    }) => {
        console.log('切换到新增表单视图', params);
        setFormViewParams({
            mode: 'add',
            ...params
        });
        setPopupContentView('addForm');
    }, []);

    // 处理导航到更新表单（地图模式）
    const handleNavigateToUpdateForm = useCallback((params: {
        code: any; // AccessCode
        orderId: number;
        addressId?: number;
        trackingNumber?: string;
        address?: string;
        addressLat?: number;
        addressLon?: number;
    }) => {
        console.log('切换到更新表单视图', params);
        setFormViewParams({
            mode: 'update',
            ...params
        });
        setPopupContentView('updateForm');
    }, []);

    // 当selectedMarkerData变化时，调用地址分析API
    useEffect(() => {
        // 重置地址分析结果
        setAnalyzedAddressLine1(undefined);

        if (!selectedMarkerData) {
            return;
        }

        // 获取地址信息（优先从集群的第一个地址获取，如果不是集群则从单个标记获取）
        let deliveryAddress: any = null;
        
        if (selectedMarkerData.isCluster && selectedMarkerData.clusterAddresses && selectedMarkerData.clusterAddresses.length > 0) {
            // 从deliveryTaskOrderList中查找第一个订单的完整信息
            const firstOrderId = selectedMarkerData.clusterAddresses[0].deliveryTaskOrderId;
            const firstOrder = deliveryTaskOrderList.find(item => item.id === firstOrderId);
            deliveryAddress = firstOrder?.orderWithAddress?.deliveryAddress;
        } else if (selectedMarkerData.deliveryTaskOrderId) {
            // 单个标记的情况
            const order = deliveryTaskOrderList.find(item => item.id === selectedMarkerData.deliveryTaskOrderId);
            deliveryAddress = order?.orderWithAddress?.deliveryAddress;
        }

        if (!deliveryAddress?.addressLine1 || !deliveryAddress?.city || !deliveryAddress?.state || !deliveryAddress?.zipcode) {
            return;
        }

        // 检查灰度配置，如果未启用则不调用地址分析API
        if (!isNavigationAddressAnalyzeEnabled) {
            return;
        }

        // 调用地址分析API
        analyzeAddress({
            variables: {
                streetAddress: deliveryAddress.addressLine1,
                unit: deliveryAddress.addressLine2 || null,
                city: deliveryAddress.city,
                state: deliveryAddress.state,
                zipCode: deliveryAddress.zipcode
            }
        }).then(response => {
            if (response.data?.analyzeAddressSimple?.entityComponents) {
                const components = response.data.analyzeAddressSimple.entityComponents;
                // 组合标准化解析后的字段：line1 + city + state + zipcode，state 和 zipcode 之间用空格，其他用逗号分隔
                if (components?.addressLine1) {
                    const stateZip = [components.stateAbbreviation, components.zipcode].filter(Boolean).join(' ');
                    const parts = [components.addressLine1, components.cityName, stateZip].filter(Boolean);
                    setAnalyzedAddressLine1(parts.join(', '));
                }
            }
        }).catch(error => {
            console.error('Failed to analyze address:', error);
            // 分析失败不影响主流程，保持静默
        });
    }, [selectedMarkerData, deliveryTaskOrderList, analyzeAddress, isNavigationAddressAnalyzeEnabled]);

    // 只在从详情页返回时恢复弹窗状态
    useEffect(() => {
        // 检查是否是从详情页返回（通过 sessionStorage 标记）
        const isReturningFromDetail = sessionStorage.getItem('returningFromDetail') === 'true';
        const isPickUpSuccess = sessionStorage.getItem('pickUpSuccess') === 'true';

        if (!isReturningFromDetail) {
            // 如果不是从详情页返回，清除可能存在的恢复标记
            sessionStorage.removeItem('returningFromDetail');
        }

        if (!isPickUpSuccess) {
            // 如果不是从详情页返回，清除可能存在的恢复标记
            sessionStorage.removeItem('pickUpSuccess');
        }

        if (isReturningFromDetail ) {
            sessionStorage.removeItem('returningFromDetail'); // 清除标记
            sessionStorage.removeItem('pickUpSuccess'); // 清除标记
            const savedMarkerData = getSelectedMarkerFromStorage();
            if (savedMarkerData && savedMarkerData.isCluster) {
                const selectedAddress =  sessionStorage.getItem("selectAddressSession")
                // 过滤出有效的订单
                const validClusterAddresses = isPickUpSuccess 
                    ? filterValidClusterAddresses(savedMarkerData.clusterAddresses || [])
                    : (savedMarkerData.clusterAddresses?.filter((order: any) => {
                        return deliveryTaskOrderList.find(item => item.id === order.deliveryTaskOrderId && !item.isTransferOut);
                    }) || []);

                // 检查选择的地址下是否还有订单
                const ordersForSelectedAddress = validClusterAddresses.filter((addr: any) => addr.address === selectedAddress);

                if (ordersForSelectedAddress.length > 0) {
                    // 选择的地址下还有订单，直接展示订单（卡片列表）
                    const freshMarkerData = {
                        ...savedMarkerData,
                        clusterAddresses: ordersForSelectedAddress,
                        clusterCount: ordersForSelectedAddress.length,
                        isCluster: true,
                        isShowNavigationBar: true
                    };
                    setSelectedMarkerData(freshMarkerData);
                    setViewMode('card');
                    setPopupVisible(true);
                    setHasRestoredPopup(true);
                } else {
                    // 选择的地址下没有订单，检查这个经纬度下是否还有不同地址
                    const remainingUniqueAddresses = new Set(validClusterAddresses.map((addr: any) => addr.address));

                    if (remainingUniqueAddresses.size <= 1) {
                        // 没有不同地址，直接展示卡片列表
                        if (validClusterAddresses.length > 0) {
                            const freshMarkerData = {
                                ...savedMarkerData,
                                clusterAddresses: validClusterAddresses,
                                clusterCount: validClusterAddresses.length,
                                isCluster: true,
                                isShowNavigationBar: false
                            };
                            setSelectedMarkerData(freshMarkerData);
                            setViewMode('card');
                            setPopupVisible(true);
                            setHasRestoredPopup(true);
                        } else {
                            clearSelectedMarkerFromStorage();
                        }
                    } else {
                        // 有不同地址，展示地址列表
                        const freshMarkerData = {
                            ...savedMarkerData,
                            clusterAddresses: validClusterAddresses,
                            clusterCount: validClusterAddresses.length,
                            isCluster: true
                        };
                        setSelectedMarkerData(freshMarkerData);
                        setViewMode('address');
                        setPopupVisible(true);
                        setHasRestoredPopup(true);
                    }
                }

            }
        }
    }, [deliveryTaskOrderList, getSelectedMarkerFromStorage, clearSelectedMarkerFromStorage, hasRestoredPopup]);

    return (
        <div className="flex flex-col h-screen bg-white">
            {/* Navbar - 固定在顶部 */}
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={handleBackNavigation}>
                {t('pages.useMenu.mapMode')}
                </NavBar>
            </div>

            {/* 875: POD异常提示隐藏 */}
            {/* <div className="flex-none">
                <PODAbnormalAlert navigateState={{ from: 'MapModePage' }} />
            </div> */}

            {/* 地图容器 - 占据剩余空间 */}
            <div className="flex-1 relative">
                {deliveryAddresses.length > 0 ? (
                    <MapboxMap
                        deliveryAddresses={deliveryAddresses}
                        onMarkerClick={handleMarkerClick}
                        onMapClick={handleMapClick}
                        mapRef={mapRef}
                        getSavedMapState={getSavedMapState}
                        pageMarker={MapBoxPageMarker.DELIVERY_MAP_MODE}
                    />
                ) : (
                    <EmptyState />
                )}
            </div>
            
            {/* Popup 弹窗 */}
            <Popup
                visible={popupVisible}
                position="bottom"
                mask={false}
                bodyStyle={{
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px',
                    height: '80vh'
                }}
                onClose={handleClosePopup}
            >
                {selectedMarkerData && (
                    <div style={{ height: '100%' }}>
                        {popupContentView === 'accessCode' && accessCodeViewParams ? (
                            <div className="flex flex-col h-full">
                                <div className="flex-none flex items-center px-4 py-3 border-b border-gray-200 bg-white">
                                    <button
                                        onClick={() => {
                                            setPopupContentView('order');
                                            setAccessCodeViewParams(null);
                                        }}
                                        className="flex items-center gap-1 text-base text-gray-900 active:opacity-70"
                                    >
                                        <LeftOutline fontSize={20} />
                                    </button>
                                    <div className="flex-1 text-center text-base text-gray-900">
                                        {t('pages.delivery.orderDetail.referenceAccessInfo')}
                                    </div>
                                    <div className="w-8"></div>
                                </div>
                                <div className="flex-1 overflow-hidden">
                                    <AccessInfoDetailView
                                        orderId={accessCodeViewParams.orderId}
                                        addressId={accessCodeViewParams.addressId}
                                        trackingNumber={accessCodeViewParams.trackingNumber}
                                        address={accessCodeViewParams.address}
                                        addressLat={accessCodeViewParams.addressLat}
                                        addressLon={accessCodeViewParams.addressLon}
                                        refreshTrigger={accessCodeRefreshTrigger}
                                        onBack={() => {
                                            // 设置标记，返回orderdetail页时检查待验证code
                                            setShouldCheckPendingCodes(true);
                                            setPopupContentView('order');
                                            setAccessCodeViewParams(null);
                                        }}
                                        onNavigateToAdd={(params) => {
                                            handleNavigateToAddForm({
                                                orderId: params.orderId,
                                                addressId: params.addressId,
                                                trackingNumber: params.trackingNumber,
                                                address: params.address,
                                                addressLat: params.addressLat,
                                                addressLon: params.addressLon
                                            });
                                        }}
                                        onNavigateToUpdate={(params) => {
                                            handleNavigateToUpdateForm({
                                                code: params.code,
                                                orderId: params.orderId,
                                                addressId: params.addressId,
                                                trackingNumber: params.trackingNumber,
                                                address: params.address,
                                                addressLat: params.addressLat,
                                                addressLon: params.addressLon
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                        ) : (popupContentView === 'addForm' || popupContentView === 'updateForm') && formViewParams ? (
                            <div className="flex flex-col h-full">
                                <div className="flex-none flex items-center px-4 py-3 border-b border-gray-200 bg-white">
                                    <button
                                        onClick={() => {
                                            // 如果是从详情页进入的，返回到详情页；否则返回到订单页
                                            if (accessCodeViewParams) {
                                                setPopupContentView('accessCode');
                                            } else {
                                                setPopupContentView('order');
                                            }
                                            setFormViewParams(null);
                                        }}
                                        className="flex items-center gap-1 text-base text-gray-900 active:opacity-70"
                                    >
                                        <LeftOutline fontSize={20} />
                                    </button>
                                    <div className="flex-1 text-center text-base font-semibold">
                                        {popupContentView === 'addForm' 
                                            ? t('pages.delivery.orderDetail.addAccessCode')
                                            : t('pages.delivery.orderDetail.updateAccessCodeTitle')
                                        }
                                    </div>
                                </div>
                                <div className="flex-1 overflow-y-auto">
                                    <AccessCodeFormView
                                        mode={formViewParams.mode}
                                        code={formViewParams.code}
                                        addressId={formViewParams.addressId}
                                        trackingNumber={formViewParams.trackingNumber}
                                        address={formViewParams.address}
                                        addressLat={formViewParams.addressLat}
                                        addressLon={formViewParams.addressLon}
                                        orderId={formViewParams.orderId}
                                        onBack={() => {
                                            // 如果是从详情页进入的，返回到详情页；否则返回到订单页
                                            if (accessCodeViewParams) {
                                                setPopupContentView('accessCode');
                                            } else {
                                                setPopupContentView('order');
                                            }
                                            setFormViewParams(null);
                                        }}
                                        onSuccess={() => {
                                            // 判断返回目标：
                                            // 1. 如果 accessCodeViewParams 存在，说明是从 codeList 进入的，返回 codeList
                                            // 2. 如果 accessCodeViewParams 不存在，说明是从卡片进入的（第一次新增），返回卡片
                                            if (accessCodeViewParams) {
                                                // 从 codeList 进入的场景：返回 codeList
                                                // 触发详情页刷新
                                                setAccessCodeRefreshTrigger(prev => prev + 1);
                                                // 设置标记，返回到详情页时检查待验证code
                                                setShouldCheckPendingCodes(true);
                                                // 返回到详情页（codeList）
                                                setPopupContentView('accessCode');
                                            } else {
                                                // 从卡片进入的场景（第一次新增）：返回卡片
                                                // 不设置标记，因为用户刚新增，可能还没有尝试，不需要立即弹框收集反馈
                                                // 返回到订单卡片
                                                setPopupContentView('order');
                                            }
                                            setFormViewParams(null);
                                        }}
                                    />
                                </div>
                            </div>
                        ) : (
                            <>
                                {viewMode === 'address' && selectedMarkerData.clusterAddresses && (
                            <AddressList
                                addresses={selectedMarkerData.clusterAddresses}
                                onAddressClick={handleAddressClick}
                            />
                        )}
                        {viewMode === 'card' && (
                            <div className="flex flex-col h-full">
                                {(() => {
                                    // 只有在多个不同地址时才显示返回按钮
                                    if (selectedMarkerData.isShowNavigationBar) {
                                        return (
                                            <div className="flex-none flex items-center px-4 py-3 border-b border-gray-200 bg-white">
                                                <button
                                                    onClick={handleBackToAddressList}
                                                    className="flex items-center gap-1 text-base text-gray-900 active:opacity-70"
                                                >
                                                    <LeftOutline fontSize={20} />
                                                </button>
                                            </div>
                                        );
                                    }
                                })()}
                                {/* 提取订单过滤逻辑为函数，减少重复计算 */}
                                <div className="flex-1 overflow-y-auto">
                                    {(() => {
                                        // 过滤有效订单的工具函数
                                        const getValidOrders = (orders: any[]) => {
                                            return orders.filter(order => {
                                                return deliveryTaskOrderList.find(item => item.id === order.deliveryTaskOrderId)
                                            });
                                        };

                                        // 处理集群数据场景
                                        if (selectedMarkerData.isCluster && selectedMarkerData.clusterAddresses) {
                                            const validOrders = getValidOrders(selectedMarkerData.clusterAddresses);
                                            const sortedOrders = sortClusterOrders(validOrders); // 应用排序
                                            // 当只有一个卡片时，需要特殊处理布局，确保底部按钮贴底
                                            const isSingleCard = sortedOrders.length === 1;
                                            return (
                                                <div className="h-full overflow-y-auto">
                                                    <div className="space-y-4 p-4">
                                                        {sortedOrders.map((order, index) => (
                                                            <div 
                                                                key={order.id || index} 
                                                                className={isSingleCard ? "h-full flex flex-col border border-gray-200 rounded-lg" : "border border-gray-200 rounded-lg"}
                                                            >
                                                                <OrderDetailCard
                                                                    deliveryTaskOrderId={order.deliveryTaskOrderId}
                                                                    trackingNumber={order.trackingNumber || ''}
                                                                    onClose={() => { /* 集群模式不关闭弹窗 */ }}
                                                                    onBeforeNavigate={handleBeforeNavigate}
                                                                    fromMapMode={true}
                                                                    onNavigateToAccessCodeDetail={handleNavigateToAccessCodeDetail}
                                                                    onNavigateToAddForm={handleNavigateToAddForm}
                                                                    clusterInfo={{
                                                                        isCluster: true,
                                                                        clusterCount: validOrders.length,
                                                                        clusterAddresses: validOrders
                                                                    }}
                                                                    popupContentView={popupContentView}
                                                                    analyzedAddressLine1={analyzedAddressLine1}
                                                                    isNavigationAddressAnalyzeEnabled={isNavigationAddressAnalyzeEnabled}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        }

                                        // 处理单个订单场景
                                        return (
                                            <OrderDetailCard
                                                ref={orderDetailCardRef}
                                                deliveryTaskOrderId={selectedMarkerData.deliveryTaskOrderId}
                                                trackingNumber={selectedMarkerData.trackingNumber || ''}
                                                onClose={handleClosePopup}
                                                onBeforeNavigate={handleBeforeNavigate}
                                                fromMapMode={true}
                                                onNavigateToAccessCodeDetail={handleNavigateToAccessCodeDetail}
                                                onNavigateToAddForm={handleNavigateToAddForm}
                                                popupContentView={popupContentView}
                                                analyzedAddressLine1={analyzedAddressLine1}
                                                isNavigationAddressAnalyzeEnabled={isNavigationAddressAnalyzeEnabled}
                                            />
                                        );

                                    })()}
                                </div>
                            </div>
                        )}
                            </>
                        )}
                    </div>
                )}

            </Popup>
        </div>
    );
};

export default MapModePage; 